// assets/vm-card-colors.js. Varmth 20 Oct (v6.4, cards lane; owner 20 Oct: "Color dots under the products with multi
// colors to see the color options visually before clicking on product page"). <vm-card-colors>, the color dots on
// product cards (snippets/vm-card-colors.liquid, rendered by snippets/vm-card-meta.liquid).
// - Desktop (hover:hover, pointer:fine): pointing at a dot, or keyboard focus on it, previews that color on the card's
//   <vm-card-tile>; leaving the row (pointer or focus) returns to the pressed dot.
// - Click / tap / Enter / Space selects a dot: aria-pressed moves, the photo stays, there is no navigation, and the
//   card's overlay, photo and title links get ?variant= of that color (Horizon ProductCard.applyVariantToLinks, else
//   by hand). The dot pressed at load (data-vm-cc-init) restores the tile and the links exactly as rendered.
// - Photos: a dot carries one 900 px URL (data-img); the 360/540/720/900 srcset is built here with the tile's own
//   sizes and decoded off-screen before the swap. Nothing downloads before the first interaction with a row.
//   Another color's photo gets mode white (contain on Oat, when the gallery's white list has it) or studio (cover);
//   zoom / front / scene crops are never inherited.
// - Hover heat (assets/vm-glow.js, imported by the tile's own data-vm-glow-js URL so there is one module and one state
//   map): in one synchronous step the old layer goes off at once, data-vm-glow becomes the new photo's layer (or is
//   removed), and the new layer lights while the card is hovered or keyboard-focused. A glow is never drawn on
//   another photo. The heat view (html[data-vm-view="heat"]) draws no glow; the plate stays on top.
// - Morph-safe: the selection is the aria-pressed dot, "another color shown" is data-vm-cc on the tile, the default
//   tile is captured whenever the tile lacks data-vm-cc. A section morph (sort, filters) resets all of it from the
//   server. A sequence number drops stale async work.
// - Phase B (Color filter): when the server pressed another dot than the tile's own color (row data-vm-cc-apply, one
//   active Color filter value), the links point at that color at once and its photo swaps in when the row nears the
//   viewport. A change of the overlay link's href (any section re-render) re-checks it.
const FINE = matchMedia('(hover: hover) and (pointer: fine)');
const RM = matchMedia('(prefers-reduced-motion: reduce)');
const WIDTHS = [360, 540, 720, 900]; // = the tile's image_tag widths (snippets/vm-heat-hover.liquid)
const MODE = /(^|\s)vm-tile--(tone|white|zoom|front|scene|studio)(?=\s|$)/g;
const DEF = new WeakMap(); // tile -> its rendered state { src, srcset, alt, cls, glow, blend }
const WARM = new Map(); // photo src -> decode promise
// Phase B (a Color filter pressed another dot at render): the photo swap waits until the row is near the viewport.
const NEAR =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (es) => {
          for (const e of es) {
            if (!e.isIntersecting) continue;
            NEAR.unobserve(e.target);
            /** @type {any} */ (e.target).applyPhoto?.();
          }
        },
        { rootMargin: '600px 0px' }
      )
    : null;

const dotOf = (e) => e.target?.closest?.('.vm-cc__dot');
const fileOf = (src) => {
  try {
    return new URL(src, location.href).pathname;
  } catch {
    return '';
  }
};

/** Dot b's photo: srcset like the tile's (no width above the file's own), mode, glow. */
function target(b) {
  const u = new URL(b.dataset.img, location.href);
  const max = Number(b.dataset.w) || 900;
  let ws = WIDTHS.filter((w) => w <= max);
  if (!ws.length) ws = [max];
  const srcset = ws
    .map((w) => {
      u.searchParams.set('width', String(w));
      return `${u.href} ${w}w`;
    })
    .join(', ');
  u.searchParams.set('width', String(ws[ws.length - 1]));
  return {
    src: u.href,
    srcset,
    alt: b.dataset.alt || '',
    mode: b.dataset.mode === 'white' ? 'white' : 'studio',
    glow: b.dataset.glow || '',
    blend: b.dataset.blend === 'n' ? 'normal' : '',
  };
}

/** Fetch + decode off-screen with the tile's sizes, once per photo. */
function warm(t, sizes) {
  let p = WARM.get(t.src);
  if (!p) {
    const im = new Image();
    im.decoding = 'async';
    if (sizes) im.sizes = sizes;
    im.srcset = t.srcset;
    im.src = t.src;
    p = im.decode
      ? im.decode()
      : new Promise((ok, no) => {
          im.onload = ok;
          im.onerror = no;
        });
    p.catch(() => WARM.delete(t.src));
    WARM.set(t.src, p);
  }
  return p;
}

/**
 * Glow layers left from another photo. vm-glow.js inserts a layer when its image has loaded, even when a fast sweep
 * across the dots has replaced that layer's state meanwhile; such a layer stays at opacity 0 but would pile up. Drop
 * every layer whose file is not the tile's current data-vm-glow (vm-glow.js re-inserts the current one when needed).
 */
function prune(tile) {
  const want = tile.dataset.vmGlow ? new URL(tile.dataset.vmGlow, location.href).href : '';
  for (const el of tile.querySelectorAll(':scope > .vm-glow__img')) if (el.src !== want) el.remove();
}

/** ?variant= on the overlay, photo and title links ('' removes it). Each link keeps its path and other params. */
function links(card, v) {
  if (typeof card.applyVariantToLinks === 'function') {
    try {
      card.applyVariantToLinks(v || null);
      return;
    } catch {
      /* fall through to the manual path */
    }
  }
  for (const a of card.querySelectorAll('a.product-card__link, a[ref="cardGalleryLink"], a[ref="productTitleLink"]')) {
    if (!a.getAttribute('href')) continue;
    const u = new URL(a.href);
    if (v) u.searchParams.set('variant', v);
    else u.searchParams.delete('variant');
    a.href = u.href;
  }
}

class VmCardColors extends HTMLElement {
  static observedAttributes = ['data-product-id'];

  seq = 0;
  warmed = '';

  /** A morph re-used this row for another product: drop pending work and the warm flag. */
  attributeChangedCallback() {
    this.seq++;
    this.warmed = '';
    this.v0 = undefined;
  }

  connectedCallback() {
    this.ac?.abort();
    const ac = (this.ac = new AbortController());
    const o = { signal: ac.signal };
    this.addEventListener(
      'pointerover',
      (e) => {
        if (e.pointerType === 'touch' || !FINE.matches) return;
        this.warmAll();
        const b = dotOf(e);
        if (b) {
          this.live(true);
          this.show(b);
        }
      },
      o
    );
    this.addEventListener(
      'pointerleave',
      (e) => {
        if (e.pointerType === 'touch' || !this.live()) return;
        if (this.contains(document.activeElement) && document.activeElement.matches(':focus-visible')) return;
        this.live(false);
        this.show(this.pressed());
      },
      o
    );
    this.addEventListener(
      'focusin',
      (e) => {
        const b = dotOf(e);
        if (!b || !b.matches(':focus-visible')) return;
        this.warmAll();
        this.live(true);
        this.show(b);
      },
      o
    );
    this.addEventListener(
      'focusout',
      (e) => {
        if (this.contains(e.relatedTarget)) return;
        this.rove(this.pressed());
        if (!this.live() || this.matches(':hover')) return;
        this.live(false);
        this.show(this.pressed());
      },
      o
    );
    // A tap or click: start the fetch before the click event.
    this.addEventListener(
      'pointerdown',
      (e) => {
        const b = dotOf(e);
        if (b?.dataset.img) warm(target(b), this.img()?.sizes).catch(() => {});
      },
      o
    );
    this.addEventListener(
      'click',
      (e) => {
        const b = dotOf(e);
        if (b) this.select(b);
      },
      o
    );
    this.addEventListener('keydown', (e) => this.key(e), o);
    this.watch();
    this.applyPressed();
  }

  disconnectedCallback() {
    this.ac?.abort();
    this.mo?.disconnect();
    NEAR?.unobserve(this);
    this.seq++;
  }

  /**
   * Phase B, morph-safe: a section re-render (filters, sort, back / forward) rewrites the card's overlay link, so a
   * change of its href re-checks the filter color. Our own link writes match the pressed dot and do nothing.
   */
  watch() {
    this.mo?.disconnect();
    const a = this.card()?.querySelector('a.product-card__link');
    if (!a || typeof MutationObserver !== 'function') return;
    this.mo = new MutationObserver(() => this.applyPressed());
    this.mo.observe(a, { attributes: true, attributeFilter: ['href'] });
  }

  /**
   * Phase B: the server pressed another dot than the tile's own color (data-vm-cc-apply: one Color filter value is
   * active). The links point at that color at once; its photo swaps in when the row nears the viewport.
   */
  applyPressed() {
    if (!this.hasAttribute('data-vm-cc-apply')) return;
    const p = this.pressed();
    const card = this.card();
    if (!p || !card || p.hasAttribute('data-vm-cc-init')) return;
    const v = p.dataset.variant || '';
    const a = card.querySelector('a.product-card__link');
    let cur = null;
    try {
      cur = a?.href ? new URL(a.href).searchParams.getAll('variant') : null;
    } catch {
      cur = null;
    }
    if (cur && !(cur.length === (v ? 1 : 0) && (!v || cur[0] === v))) links(card, v);
    const tile = this.tile();
    if (p.dataset.img && tile && tile.getAttribute('data-vm-cc') !== (p.dataset.color || '')) {
      if (NEAR) NEAR.observe(this);
      else this.applyPhoto();
    }
  }

  applyPhoto() {
    const p = this.pressed();
    if (p && !p.hasAttribute('data-vm-cc-init') && !this.live()) this.show(p);
  }

  card() {
    return this.closest('product-card');
  }

  tile() {
    return this.card()?.querySelector('vm-card-tile') || null;
  }

  img() {
    return this.tile()?.querySelector(':scope > .vm-tile__img') || null;
  }

  dots() {
    return [...this.querySelectorAll('.vm-cc__dot')];
  }

  pressed() {
    return this.querySelector('.vm-cc__dot[aria-pressed="true"]') || this.querySelector('.vm-cc__dot');
  }

  /** data-vm-cc-live on the tile while a dot is previewed (hides the default-color hover layer). No arg = read. */
  live(on) {
    const t = this.tile();
    if (on === undefined) return !!t?.hasAttribute('data-vm-cc-live');
    t?.toggleAttribute('data-vm-cc-live', on);
    return on;
  }

  rove(b) {
    for (const d of this.dots()) d.tabIndex = d === b ? 0 : -1;
  }

  warmAll() {
    if (this.warmed === this.dataset.productId) return;
    this.warmed = this.dataset.productId || '';
    const sizes = this.img()?.sizes;
    for (const b of this.querySelectorAll('.vm-cc__dot[data-img]')) warm(target(b), sizes).catch(() => {});
  }

  /**
   * The variant of the card's own link as rendered (read while the init dot is still the pressed one). With a Color
   * filter, Shopify's product.url already holds ?variant= and vm-heat-hover may append a second one for the photo's
   * color; the product page uses the last one, and so does the init dot (snippets/vm-card-colors.liquid).
   */
  initVariant(b) {
    const init = this.querySelector('.vm-cc__dot[data-vm-cc-init]');
    const card = this.card();
    if (init && init.getAttribute('aria-pressed') === 'true' && card) {
      const a = card.querySelector('a.product-card__link');
      if (a?.href) {
        try {
          this.v0 = new URL(a.href).searchParams.getAll('variant').pop() || '';
        } catch {
          /* keep the last known value */
        }
      }
    }
    return this.v0 ?? b.dataset.variant ?? '';
  }

  select(b) {
    const v0 = this.initVariant(b);
    for (const d of this.dots()) d.setAttribute('aria-pressed', String(d === b));
    this.rove(b);
    const card = this.card();
    if (card) links(card, b.hasAttribute('data-vm-cc-init') ? v0 : b.dataset.variant || '');
    this.show(b);
  }

  key(e) {
    const ds = this.dots();
    const i = ds.indexOf(/** @type {any} */ (document.activeElement));
    if (i < 0) return;
    const rtl = getComputedStyle(this).direction === 'rtl';
    let j;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        j = rtl ? i - 1 : i + 1;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        j = rtl ? i + 1 : i - 1;
        break;
      case 'Home':
        j = 0;
        break;
      case 'End':
        j = ds.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    j = (j + ds.length) % ds.length;
    this.rove(ds[j]);
    ds[j].focus(); // focusin previews it
  }

  /** Show dot b's color on the tile. The init dot restores the tile as rendered; a dot with no photo changes nothing. */
  async show(b) {
    const seq = ++this.seq;
    const card = this.card();
    const tile = card?.querySelector('vm-card-tile');
    const img = tile?.querySelector(':scope > .vm-tile__img');
    if (!b || !tile || !img) return;
    if (!tile.hasAttribute('data-vm-cc')) {
      DEF.set(tile, {
        src: img.getAttribute('src') || '',
        srcset: img.getAttribute('srcset') || '',
        alt: img.getAttribute('alt') ?? '',
        cls: tile.className,
        glow: tile.dataset.vmGlow || '',
        blend: tile.dataset.vmBlend || '',
      });
    }
    const d = DEF.get(tile);
    if (!d) return;
    let next;
    let isDef = false;
    if (b.hasAttribute('data-vm-cc-init')) {
      next = d;
      isDef = true;
    } else if (b.dataset.img) {
      const t = target(b);
      if (fileOf(t.src) === fileOf(d.src)) {
        next = d; // this color's photo is the one the card shows already
        isDef = true;
      } else {
        const cls = `${d.cls.replace(MODE, ' ').replace(/\s+/g, ' ').trim()} vm-tile--${t.mode}`;
        next = { src: t.src, srcset: t.srcset, alt: t.alt, cls, glow: t.glow, blend: t.blend };
      }
    } else return; // no photo for this color: keep what is shown (the links still change on select)
    if (img.getAttribute('src') === next.src && tile.className === next.cls) return;
    if (!isDef) {
      try {
        await warm(next, img.sizes);
      } catch {
        return;
      }
    }
    const was = tile.dataset.vmGlow || '';
    const js = tile.dataset.vmGlowJs || this.dataset.glowJs || '';
    const g = FINE.matches && js && was !== next.glow ? await import(js).catch(() => null) : null;
    if (seq !== this.seq || !tile.isConnected || !this.isConnected || !b.isConnected) return;
    // Only the pressed dot, or a dot previewed while the row is live (a morph clears both).
    if (b.getAttribute('aria-pressed') !== 'true' && !this.live()) return;

    // One synchronous step, so no frame shows a glow on the wrong photo.
    if (g && was) {
      g.glow(tile, 'hover', false);
      g.glow(tile, 'focus', false);
      for (const el of tile.querySelectorAll(':scope > .vm-glow__img')) {
        el.getAnimations?.().forEach((a) => a.cancel());
        el.style.opacity = '0';
      }
    }
    if (next.srcset) img.setAttribute('srcset', next.srcset);
    else img.removeAttribute('srcset');
    img.setAttribute('src', next.src);
    img.alt = next.alt;
    tile.className = next.cls;
    if (next.glow) {
      tile.dataset.vmGlow = next.glow;
      if (next.blend) tile.dataset.vmBlend = next.blend;
      else delete tile.dataset.vmBlend;
      if (!tile.dataset.vmGlowJs && this.dataset.glowJs) {
        tile.dataset.vmGlowJs = this.dataset.glowJs;
        tile.dataset.vmGlowCss = this.dataset.glowCss || '';
      }
    } else {
      delete tile.dataset.vmGlow;
      delete tile.dataset.vmBlend;
    }
    if (isDef) tile.removeAttribute('data-vm-cc');
    else tile.setAttribute('data-vm-cc', b.dataset.color || '');
    if (g || was) {
      prune(tile);
      // and again once a layer still loading from an earlier dot has landed
      setTimeout(() => seq === this.seq && tile.isConnected && prune(tile), 800);
    }
    if (g && next.glow) {
      if (card.matches(':hover')) g.glow(tile, 'hover', true);
      const a = document.activeElement;
      if (a && card.contains(a) && a.matches(':focus-visible')) g.glow(tile, 'focus', true);
    }
    if (!RM.matches && img.animate) img.animate([{ opacity: 0.86 }, { opacity: 1 }], { duration: 150, easing: 'ease-out' });
  }
}

if (!customElements.get('vm-card-colors')) customElements.define('vm-card-colors', VmCardColors);
